import { useIntl } from 'react-intl'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import Heading from 'LuxKit/Typography/Heading'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import React, { useMemo } from 'react'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { rem } from 'polished'
import styled from 'styled-components'
import HeroTourOfferTilePricing from '../Support/HeroTourOfferTilePricing'
import useCheapestOfTourV2Offer from 'hooks/TourV2/useCheapestOfTourV2Offer'
import useTourV2VariationDetails from 'hooks/TourV2/useTourV2VariationDetails'
import TextLink from 'LuxKit/TextLink'
import Group from 'components/utils/Group'
import TourV2UrgencyTags from 'components/Tours/TourV2UrgencyTags'
import useTourV2Details from 'hooks/TourV2/useTourV2Details'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import OfferRating from 'components/Common/NumberRating/OfferRating'
import { isOfferRatingDisplayable } from 'lib/order/reviewUtils'
import findCheapestTourV2PurchasableOption from 'lib/tours/findCheapestTourV2PurchasableOption'
import { generateLoyaltyPointsCalculatorTourOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import LoyaltyPointsEarn from 'loyalty/components/Earn/LoyaltyPointsEarn'

const DetailContainer = styled(Group)`
  height: 100%;
  padding-top: ${rem(16)};
  background-color: ${props => props.theme.palette.neutral.default.eight};
  ${mediaQueryUp.desktop} {
    flex: 1;
    padding: ${rem(16)} ${rem(32)} ${rem(32)} ${rem(32)};
  }
`

const DetailSegment = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex: 1;
  gap: ${rem(8)};
`

const DetailsCaptionContainer = styled(BodyText)`
  display: flex;

  > * + *::before {
    color: ${props => props.theme.palette.neutral.default.one};
    content: '·';
    margin: 0 ${rem(4)};
    speak-as: bullets;
    speak: none;
  }
`

const HeaderActions = styled.div`
  display: flex;
  flex-direction: row-reverse;
  justify-content: space-between;
`

const MinHeightGroup = styled(Group)`
  min-height: ${rem(150)};
  flex-grow: 1;
`

interface Props {
  offer: App.Tours.TourV2Offer | App.Tours.TourV2OfferSummary
}

function HeroTourOfferDetails(props: Props) {
  const { offer } = props
  const intl = useIntl()
  const { cheapestVariation } = useCheapestOfTourV2Offer(offer)
  const {
    startLocation,
    endLocation,
    hasTheSameStartAndEndLocation,
    placesCount,
    countriesCount,
    durationInDaysCount,
    daysCount,
  } = useTourV2VariationDetails(cheapestVariation)

  const { hasMoreVariations, hasAlternativeRoute, upgradesAvailable } = useTourV2Details(offer)

  const pointsEarnCalculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(
    () => {
      const cheapestPurchasableOption = findCheapestTourV2PurchasableOption(offer)
      const nights = daysCount && daysCount > 0 ? daysCount - 1 : undefined
      return [
        generateLoyaltyPointsCalculatorTourOptions(offer,
          {
            price: cheapestPurchasableOption?.price,
            memberPrice: cheapestPurchasableOption?.memberPrice,
            packageId: cheapestPurchasableOption?.fkDepartureId,
            offerId: cheapestPurchasableOption?.fkTourId,
            nights,
          }),
      ]
    },
    [offer, daysCount],
  )

  return (
    <DetailContainer direction="vertical" gap={16}>
      <CSSBreakpoint min="desktop">
        <Group direction="horizontal" horizontalAlign="space-between">
          {isOfferRatingDisplayable(offer.rating) && <OfferRating variant="medium" rating={offer.rating} />}
          <TripGuard>
            <HeaderActions>
              <BookmarkButton offer={offer} />
            </HeaderActions>
          </TripGuard>
        </Group>
      </CSSBreakpoint>
      <DetailSegment data-testid="OfferInfoBar">
        <MinHeightGroup direction="vertical" gap={4} horizontalAlign="start">
          <TourV2UrgencyTags offer={offer} />
          <Caption variant="large" lineClamp={3} colour="neutral-one">
            {hasTheSameStartAndEndLocation && <>{intl.formatMessage(
              {
                id: 'xP/4XZK5gM',
                defaultMessage: 'Starts and ends in {location}',
                description: 'Hero tour tile location label when start and end locations are the same',
              },
              { location: endLocation },
            )}{' '}
            </>}
            {!hasTheSameStartAndEndLocation && <>{intl.formatMessage(
              {
                id: 'gbkTlgN3+G',
                defaultMessage: 'Starts in {startLocation}, Ends in {endLocation}',
                description: 'Hero tour tile location label showing distinct start and end locations',
              },
              { startLocation, endLocation },
            )}{' '}
            </>}
          </Caption>
          <Heading variant="heading6" lineClamp={3} colour="neutral-one">{offer.name}</Heading>
          <Caption variant="large" lineClamp={1} weight="bold">{offer.brand.name}</Caption>
          {isOfferRatingDisplayable(offer.rating) && <CSSBreakpoint max="tablet">
            <OfferRating variant="xs" rating={offer.rating} />
          </CSSBreakpoint>}
          <DetailsCaptionContainer variant="medium" colour="neutral-one">
            <div>{durationInDaysCount}</div>
            {placesCount && <div>{placesCount}</div>}
            {countriesCount && <div>{countriesCount}</div>}
          </DetailsCaptionContainer>
          {hasAlternativeRoute && <TextLink size="medium">
            Alternative route available
          </TextLink>}
          {!hasAlternativeRoute && hasMoreVariations && <TextLink size="medium">
            More options available
          </TextLink>}
          {!hasMoreVariations && upgradesAvailable && <TextLink size="medium">
            Upgrades available
          </TextLink>}
          <LoyaltyPointsEarn calculationRequests={pointsEarnCalculationRequests} calculationType="estimate" />
        </MinHeightGroup>
        <Group direction="vertical" gap={8}>
          <HeroTourOfferTilePricing offer={offer} />
        </Group>
      </DetailSegment>
    </DetailContainer>
  )
}

export default HeroTourOfferDetails
